import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DataLoadable,
  isLoadSuccess,
  isNotStarted,
  loadError,
  loading,
  loadSuccess,
  notStarted,
  StreamLoadable
} from '../utils/loadable';

export type LoadableDataMapper<T> = (data: T) => T;
export type MapLoadableData<T> = (mapper: LoadableDataMapper<T>) => void;

export type LoadableActionResult<TData, TParams extends any[]> = readonly [
  DataLoadable<TData>,
  (...args: TParams) => void,
  (processor: LoadableDataMapper<TData>) => void,
  (newData: TData) => void
];

export const useLoadableAction = <TData, TParams extends any[]>(
  loader: (...args: TParams) => Promise<TData>
): LoadableActionResult<TData, TParams> => {
  const [loadable, setLoadable] = useState<DataLoadable<TData>>(notStarted());
  const loadableRef = useRef(loadable);
  loadableRef.current = loadable;

  const doLoad = useCallback(
    (...args: TParams) => {
      setLoadable(loading(null));
      loader(...args).then(
        (data) => {
          setLoadable(loadSuccess(data));
        },
        (error: Error) => {
          setLoadable(loadError(error, null));
        }
      );
    },
    [loader]
  );

  const doUpdate = useCallback((processor: LoadableDataMapper<TData>) => {
    if (isLoadSuccess(loadableRef.current)) {
      const newData = processor(loadableRef.current.data);
      setLoadable(loadSuccess(newData));
    }
  }, []);

  const setPrefetch = useCallback((newData: TData) => {
    // only set prefetch data if no other load has been initiated
    // if component has just mounted, then this should be the case
    // but if any user action caused a new load to be initiated while wating for prefetch
    // prefetch result should be discarded and this loadable should wait for the new load from user action
    if (isNotStarted(loadableRef.current)) {
      setLoadable(loadSuccess(newData));
    }
  }, []);

  return useMemo(() => [loadable, doLoad, doUpdate, setPrefetch] as const, [doLoad, doUpdate, loadable, setPrefetch]);
};

export const noPrefetch = Promise.resolve(null);

/**
 * A hook for components that wants to load data when it is mounted, with possibly prefetched data
 *
 * before  `useEffect(() => loadableAction.doLoad(param1, param2), [loadableAction.doLoad, param1, param2])`
 * with this  `useAutoReload(loadableAction, prefetch, param1, param2);`
 *
 * @param loadableAction original loadableAction, returned from `useLoadableAction`
 * @param prefetchPromise promise containing possibly prefetched data
 * @param params (spread) params to auto load with. reload on reference equality change on any param.
 */
export const useAutoReloadWithPrefetch = <TData, TParams extends any[]>(
  loadableAction: LoadableActionResult<TData, TParams>,
  prefetchPromise: Promise<TData | null>,
  ...params: TParams
) => {
  const [, doLoad, , setPrefetch] = loadableAction;

  // we only want to use this once, and don't care if this changes (e.g. from inlined placeholder)
  const prefetchRef = useRef<Promise<TData | null>>(prefetchPromise);

  useEffect(() => {
    void prefetchRef.current
      .then((preloaded) => {
        if (preloaded === null) {
          void doLoad(...params);
        } else {
          setPrefetch(preloaded);
          prefetchRef.current = noPrefetch;
        }
      })
      .catch(() => doLoad(...params));

    // for QOL we want to only use reference equality of individual params
    // instead of forcing call site to always produce a memo-ed array of params
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doLoad, setPrefetch, ...params]);
};

export const useLoadableStreamAction = <TData, TParams extends any[]>(
  loader: (...args: TParams) => Promise<TData>,
  combineData: (oldData: TData | null, newData: TData | null) => TData,
  checkHasNewData: (oldData: TData | null, newData: TData | null) => boolean
) => {
  const [loadable, setLoadable] = useState<StreamLoadable<TData>>(notStarted());
  const [hasNewData, setHasNewData] = useState<boolean>(true);
  const loadableRef = useRef(loadable);
  loadableRef.current = loadable;

  const doLoad = useCallback(
    (...args: TParams) => {
      loader(...args).then(
        (newData) => {
          const data = combineData(loadableRef.current.data, newData);
          setHasNewData(checkHasNewData(loadableRef.current.data, newData));
          setLoadable(loadSuccess(data));
        },
        (error: Error) => {
          setLoadable(loadError(error, loadableRef.current.data));
        }
      );
    },
    [checkHasNewData, combineData, loader]
  );

  const normalLoad = useCallback(
    (...args: TParams) => {
      setLoadable(loading<TData>(loadableRef.current.data));
      doLoad(...args);
    },
    [doLoad]
  );

  const resetAndLoad = useCallback(
    (...args: TParams) => {
      setLoadable(loading<TData>(null));
      doLoad(...args);
    },
    [doLoad]
  );

  const doUpdate = useCallback((processor: LoadableDataMapper<TData>) => {
    if (isLoadSuccess(loadableRef.current)) {
      const newData = processor(loadableRef.current.data);
      setLoadable(loadSuccess(newData));
    }
  }, []);

  return useMemo(
    () => [loadable, hasNewData, normalLoad, resetAndLoad, doUpdate] as const,
    [doUpdate, hasNewData, loadable, normalLoad, resetAndLoad]
  );
};
